import { useMemo, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, Check, Download, FileCode, FileJson, FileType, Braces } from 'lucide-react';
import { SchemaField } from '@/lib/data-standards/schemas';
import {
  generateTypeScript,
  generateJsonSchemaString,
  generateOpenApiString,
  generateJsonTemplate,
  downloadFile,
} from '@/lib/builder';
import { CodeBlock } from '@/components/docs/CodeBlock';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type ExportFormat = 'typescript' | 'jsonschema' | 'openapi' | 'template';

interface CodePreviewProps {
  schema: object;
  fields: SchemaField[];
  selectedFields: string[];
  requiredOverrides: Record<string, boolean>;
  standardName: string;
  standardVersion: string;
  standardSlug: string;
}

export function CodePreview({
  schema,
  fields,
  selectedFields,
  requiredOverrides,
  standardName,
  standardVersion,
  standardSlug,
}: CodePreviewProps) {
  const [format, setFormat] = useState<ExportFormat>('typescript');
  const [copied, setCopied] = useState(false);

  const generatedCode = useMemo(() => {
    if (selectedFields.length === 0) {
      return '';
    }

    switch (format) {
      case 'typescript':
        return generateTypeScript(schema, fields, {
          standardName,
          standardVersion,
          selectedFields,
          requiredOverrides,
        });
      case 'jsonschema':
        return generateJsonSchemaString(schema, selectedFields, requiredOverrides);
      case 'openapi':
        return generateOpenApiString(schema, selectedFields, requiredOverrides, standardName, standardVersion);
      case 'template':
        return generateJsonTemplate(schema, selectedFields, requiredOverrides);
      default:
        return '';
    }
  }, [schema, fields, selectedFields, requiredOverrides, standardName, standardVersion, format]);

  const handleCopy = async () => {
    if (!generatedCode) return;
    await navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!generatedCode) return;

    switch (format) {
      case 'typescript':
        downloadFile(generatedCode, `${standardSlug}-subset.ts`, 'text/typescript');
        break;
      case 'jsonschema':
        downloadFile(generatedCode, `${standardSlug}-schema.json`, 'application/json');
        break;
      case 'openapi':
        downloadFile(generatedCode, `${standardSlug}-openapi.json`, 'application/json');
        break;
      case 'template':
        downloadFile(generatedCode, `${standardSlug}-template.json`, 'application/json');
        break;
    }
    toast.success('File downloaded');
  };

  const getLanguage = () => {
    switch (format) {
      case 'typescript':
        return 'typescript';
      case 'jsonschema':
      case 'openapi':
      case 'template':
        return 'json';
      default:
        return 'plaintext';
    }
  };

  const fileSize = useMemo(() => {
    if (!generatedCode) return '0 B';
    const bytes = new TextEncoder().encode(generatedCode).length;
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  }, [generatedCode]);

  return (
    <div className="flex flex-col h-full border-l">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 p-4 border-b">
        <Tabs value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
          <TabsList>
            <TabsTrigger value="typescript" className="gap-1.5">
              <FileCode className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">TypeScript</span>
              <span className="sm:hidden">TS</span>
            </TabsTrigger>
            <TabsTrigger value="jsonschema" className="gap-1.5">
              <FileJson className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">JSON Schema</span>
              <span className="sm:hidden">JSON</span>
            </TabsTrigger>
            <TabsTrigger value="openapi" className="gap-1.5">
              <FileType className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">OpenAPI</span>
              <span className="sm:hidden">API</span>
            </TabsTrigger>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="template" className="gap-1.5">
                  <Braces className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Template</span>
                  <span className="sm:hidden">{"{{}}"}</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p>Generate JSON with {"{{field}}"} placeholders for templating engines like Mustache or Handlebars.</p>
              </TooltipContent>
            </Tooltip>
          </TabsList>
        </Tabs>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!generatedCode}
            className="gap-1.5"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={!generatedCode}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </Button>
        </div>
      </div>

      <div className="px-4 py-2 text-sm text-muted-foreground border-b flex items-center justify-between">
        <span>
          {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected
        </span>
        <span>{fileSize}</span>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {selectedFields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileCode className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-2">No fields selected</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Select fields from the left panel to generate your custom schema.
              </p>
            </div>
          ) : (
            <CodeBlock
              code={generatedCode}
              language={getLanguage()}
              showLineNumbers
            />
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
